/**
 * ContextIndicator — brain icon with circular progress ring
 *
 * Shows how full the LLM context window is. The ring fills as tokens accumulate.
 * Color shifts from pink → orange → coral as usage increases.
 */
import { useState, useRef, useEffect, useCallback, useId } from 'react'
import { createPortal } from 'react-dom'
import { Sparkles, Loader2 } from 'lucide-react'
import { useTheme, getThemeClass, useAnchoredPosition, Tooltip } from '@papan/core-ui/ui'

interface Props {
  estimatedTokens: number
  contextLimit: number
  compacted?: boolean
  /** `focus` is the optional steer (#442) — what the summary should prioritise. */
  onSummarize?: (focus?: string) => void
  /** A manual summarize is in flight. Compaction is an LLM pass over the whole
   *  transcript — tens of seconds — and it used to run with no feedback at all:
   *  the menu closed and nothing happened until the transcript changed. */
  summarizing?: boolean
}

export function ContextIndicator({ estimatedTokens, contextLimit, compacted, onSummarize, summarizing }: Props) {
  if (!contextLimit || contextLimit === 0) return null

  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const [menu, setMenu] = useState<boolean>(false)
  const [focus, setFocus] = useState('')
  const focusId = useId()
  const containerRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  // `contextLimit` is the COMPACTION THRESHOLD (see the /usage route), not the
  // model's raw context window. That is what makes this ratio mean something a
  // user can act on: 100% is the moment the session summarises itself, rather
  // than a number that quietly stopped at 60% and then compacted anyway.
  const ratio = Math.min(estimatedTokens / contextLimit, 1)
  const pct = Math.round(ratio * 100)
  // Warn before it happens, not while it happens. 80% matches the point the
  // ring already turns red — the threshold existed and was unreachable while
  // the ratio was computed against the wrong ceiling.
  const nearLimit = ratio >= 0.8

  /** One sentence, used by the tooltip and the menu so they cannot drift. */
  const memoryLine = summarizing
    ? 'Summarizing the session\u2026'
    : compacted && !nearLimit
      ? `AI session memory: ${pct}% — earlier turns already summarized`
      : nearLimit
        ? `AI session memory: ${pct}% — will summarize earlier turns soon`
        : `AI session memory: ${pct}%`

  // Cartoon brain color — pink-orange that shifts warmer as context fills
  const color = ratio < 0.5 ? '#ffa640' : ratio < 0.8 ? '#e08e30' : '#d04020'

  // SVG circular progress
  const size = 20
  const strokeWidth = 2
  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  const offset = circumference * (1 - ratio)

  // Close menu on outside click or Escape
  useEffect(() => {
    if (!menu) return
    const onMouseDown = (e: MouseEvent) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target as Node) &&
        containerRef.current && !containerRef.current.contains(e.target as Node)
      ) setMenu(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenu(false)
    }
    document.addEventListener('mousedown', onMouseDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onMouseDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [menu])

  // Shared anchored-panel convention. `placement: 'top'` pins it ABOVE the ring
  // — this sits in the composer, so a flip downward would cover the thing it
  // describes. Replaces a hand-rolled `top: rect.top - 4` plus a
  // `translateY(-100%)`, which is the same placement expressed twice.
  const { pos: menuPos, exiting: menuExiting, origin: menuOrigin } = useAnchoredPosition({
    open: menu,
    anchorRef: containerRef,
    floatingRef: menuRef,
    onDismiss: useCallback(() => setMenu(false), []),
    placement: 'top',
    gap: 4,
    exitTransitionMs: 160,
  })

  // Open while running (the spinner in there is the feedback); close on the
  // falling edge, so a finished summarize does not leave the user parked one
  // click away from a second paid compaction.
  const wasSummarizing = useRef(false)
  useEffect(() => {
    if (wasSummarizing.current && !summarizing) {
      setMenu(false)
      // …and drop the steer it consumed. Left set, re-opening the menu shows
      // the previous focus pre-filled, so a second "Summarize session" silently
      // sends a steer written for the last compaction.
      setFocus('')
    }
    wasSummarizing.current = !!summarizing
  }, [summarizing])

  const openMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setMenu(true)
  }, [])

  return (
    <>
      {/* The shared <Tooltip> (portaled, so the chat card's overflow:hidden can't clip it); inert while the menu is
          open. The div keeps its onClick, so a touch tap never shows the tip (childIsAction). */}
      <Tooltip content={menu ? null : memoryLine} always placement="top" className="inline-flex shrink-0">
      <div
        ref={containerRef}
        className="relative inline-flex items-center justify-center shrink-0 cursor-pointer"
        onClick={openMenu}
        onContextMenu={openMenu}
      >
        <svg width={size} height={size} className="transform -rotate-90">
          {/* Background ring */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            className="opacity-15"
          />
          {/* Progress ring */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            className="transition-all duration-500"
          />
        </svg>
        {/* Custom minimalist side-profile brain */}
        <svg
          width={12}
          height={12}
          viewBox="269 164 20 19"
          fill="none"
          stroke={color}
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          className="absolute pointer-events-none transition-colors duration-500"
        >
          {/* Outer brain silhouette */}
          <path d="M 287.766 173.158 C 287.733 174.111 287.325 176.188 286.676 176.935 C 286.301 177.367 284.743 177.969 284.708 178.167 C 284.673 178.365 285.532 180.613 285.076 180.906 C 284.567 181.234 283.394 181.931 282.879 181.611 C 282.114 181.135 281.965 179.371 281.269 178.798 C 280.79 178.402 279.169 178.237 278.45 178.091 C 277.859 177.971 274.246 177.798 273.444 177.298 C 272.545 176.738 272.026 175.653 271.686 174.649 C 271.507 174.121 271.66 171.782 271.897 171.243 C 272.954 168.844 274.268 166.839 278.369 166.534 C 279.092 166.48 281.688 166.946 282.033 167.073 C 282.033 167.073 285.272 168.201 286.479 169.504 C 287.254 170.34 287.665 172.318 287.766 173.158 Z" />
          {/* Inner folds */}
          <path d="M 281.431 173.526 C 276.145 170.412 273.923 176.438 278.32 177.056" />
          <path d="M 279.985 172.877 C 280.978 172.317 282.061 170.045 280.244 170.045" />
        </svg>
      </div>
      </Tooltip>

      {/* Context menu — portaled, themed to match MessageActionsMenu */}
      {menu && createPortal(
        <div
          ref={menuRef}
          // Wider than the old 112px: that width was set for a menu of one-word
          // items, and squeezing a text field into it is most of why the focus
          // control read as decoration.
          className={`${themeClass} fixed z-[9999] w-[248px] themed-bg-secondary themed-border border rounded-lg shadow-xl py-1${menuExiting ? ' anchor-exit' : ''}`}
          style={menuPos
            ? { top: menuPos.top, left: menuPos.left, ...(menuOrigin ? { transformOrigin: `${menuOrigin.x}px ${menuOrigin.y}px` } : {}) }
            : { top: 0, left: 0, visibility: 'hidden' }}
        >
          <div className="px-3 py-1.5 text-[10px] themed-text-muted">
            {memoryLine}
          </div>
          <div className="border-t themed-border my-0.5" />
          {onSummarize && (
            // The ONLY reachable entry point for a compaction focus (#442):
            // `/compact <focus>` sits behind COMMAND_SURFACE_ENABLED, which is
            // `false`. A feature whose only control is switched off is not
            // shipped — this repo has been caught by that twice.
            //
            // A VISIBLE label and a worked example, not a lone placeholder. The
            // first cut was a bare field reading "Focus (optional)", and a
            // placeholder is the one piece of copy that vanishes the moment
            // someone types — so it cannot teach what the field is for, and it
            // is invisible to anyone scanning for a control. The example is
            // concrete because "focus" alone does not tell you what to write.
            <div className="px-3 pt-1 pb-1.5">
              <label htmlFor={focusId} className="block text-[10px] themed-text mb-1">
                Focus <span className="themed-text-muted">— optional</span>
              </label>
              <input
                id={focusId}
                value={focus}
                onChange={(e) => setFocus(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter' || summarizing) return
                  e.preventDefault()
                  onSummarize(focus.trim() || undefined)
                }}
                disabled={summarizing}
                placeholder="e.g. the auth bug fix"
                data-testid="compaction-focus"
                className="w-full px-2 py-1 text-xs rounded themed-input disabled:opacity-60"
              />
              {/* Two facts, one line. The second half is not padding: a field
                  called "Focus" invites the reading that everything else gets
                  DISCARDED, which is the opposite of what the prompt asks for
                  ("keep every section… summarise the rest more briefly rather
                  than dropping it"). Removing it leaves the control scarier
                  than the behaviour. */}
              <p className="mt-1 text-[10px] themed-text-muted leading-snug">
                Gets the most detail. Nothing is dropped.
              </p>
            </div>
          )}
          {onSummarize && (
            <button
              onClick={() => {
                if (summarizing) return
                // Deliberately does NOT close the menu while running — the
                // spinner below is the only feedback there is, and closing it
                // puts the user back where they started with no sign anything
                // happened.
                onSummarize(focus.trim() || undefined)
              }}
              disabled={summarizing}
              aria-busy={summarizing || undefined}
              className="w-full flex items-center gap-1.5 px-3 py-1.5 text-xs themed-text hover:themed-bg-tertiary transition-colors text-left disabled:opacity-60 disabled:cursor-default"
            >
              {summarizing
                ? <Loader2 size={12} className="animate-spin" />
                : <Sparkles size={12} />}
              {summarizing ? 'Summarizing\u2026' : 'Summarize session'}
            </button>
          )}
        </div>,
        document.body,
      )}
    </>
  )
}
